<script lang="ts" setup>
import { Company } from '@/core/collections/contracts-api/companies/company.interface';
import { CostCenter } from '@/core/collections/contracts-api/companies/invoice-groups/cost-centers/cost-center.interface';
import { InvoiceGroup } from '@/core/collections/contracts-api/companies/invoice-groups/invoice-group.interface';
import { Shift } from '@/core/collections/contracts-api/companies/shifts/shift.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import { Worksheet } from '@/core/collections/contracts-api/customers/employees/contract-extensions/contract-extension.interface';
import { WorkCycle } from '@/core/collections/contracts-api/customers/work-cycles/work-cycle.interface';
import {
   cloneWorkCycleForForm,
   getDefaultWorkCycleFormData,
   mapWorkCycleFormErrors,
   mapWorkCyclePayloadForSave,
   useWorkCycleSchema,
} from '@/core/collections/contracts-api/customers/work-cycles/work-cycle.schema';
import { useStore } from '@/core/hooks/useStore';
import { Timetable } from '@/core/models/timetable.interface';
import { NxtGenericFormModal, NxtLink } from '@nxtpeople/nxt-styles';
import { computed, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import ContractExtensionWorksheet from './ContractExtensionWorksheet.vue';
import { useWorkCycle } from './useWorkCycle';

//#region Globals
const { t } = useI18n();
const { dispatch } = useStore();
const { formatTimetable, mapTimetables } = useWorkCycle();
const { WORK_CYCLE_SCHEMA } = useWorkCycleSchema();

const formMountKey = ref(0);
const modalInitialData = ref<Partial<WorkCycle>>(getDefaultWorkCycleFormData());
const isModalReady = ref(false);
const isInitializing = ref(true);
//#endregion

//#region Props & Emits
const emits = defineEmits<{ (e: 'onCancel'): void; (e: 'onSave', workCycle: WorkCycle): void }>();
const props = defineProps({
   loading: { type: Boolean, default: false },
   customer: { type: Object as PropType<Customer>, default: null },
   initialWorkCycle: { type: Object as PropType<WorkCycle>, default: null },
});
//#endregion

//#region Draggable
const drag = ref(false);
//#endregion

//#region Worksheets
function deleteWorksheet(workCycle: WorkCycle, worksheetIndex: number) {
   workCycle.worksheets?.splice(worksheetIndex, 1);
}

function duplicateWorksheet(workCycle: WorkCycle, worksheetIndex: number) {
   const worksheet = workCycle.worksheets?.[worksheetIndex];
   if (!worksheet) return;
   workCycle.worksheets?.push({ ...worksheet });
}

function updateWorkSheet(workCycle: WorkCycle, payload: { key: string; value: any; worksheetIndex: number }) {
   const { key, value, worksheetIndex } = payload;

   const worksheet = workCycle.worksheets?.[worksheetIndex];
   if (!worksheet) return;
   if (isEquivalentValue((worksheet as any)[key], value)) return;
   (worksheet as any)[key] = value;
}

function addWorksheet(workCycle: WorkCycle) {
   const timetables = [...Array(7)].map((_, index) => ({ weekday: index + 1 })) as Array<Timetable>;
   workCycle.worksheets?.push({ id: new Date().getTime(), timetables } as Worksheet);
}

function isEquivalentValue(currentValue: any, newValue: any) {
   const currentIsEmpty = currentValue === undefined || currentValue === null || currentValue === '';
   const newIsEmpty = newValue === undefined || newValue === null || newValue === '';
   if (currentIsEmpty && newIsEmpty) return true;
   return currentValue === newValue;
}
//#endregion

//#region Dropdown lists
const invoiceGroups = ref<Array<InvoiceGroup>>([]);
const costCenters = ref<Array<CostCenter>>([]);
const shifts = ref<Array<Shift>>([]);

function mapInitialWorkCycleFormData() {
   if (!props.initialWorkCycle) {
      return getDefaultWorkCycleFormData();
   }

   const worksheets = props.initialWorkCycle.worksheets?.map((worksheet) => {
      const timetables = mapTimetables(worksheet.timetables);
      return { ...worksheet, timetables };
   });

   return cloneWorkCycleForForm({ ...props.initialWorkCycle, worksheets });
}

async function initializeModal() {
   modalInitialData.value = mapInitialWorkCycleFormData();
   isModalReady.value = true;

   try {
      const company: Company = await dispatch('company/getCompany', {
         companyId: props.customer.company_id,
         query: { embed: 'invoiceGroups, costCenters, shifts' },
      });
      invoiceGroups.value = company.invoice_groups;
      costCenters.value = company.cost_centers;
      shifts.value = company.shifts;

      await nextTick();
      await nextTick();
      formMountKey.value += 1;
      isModalReady.value = true;
      isInitializing.value = false;
   } catch (ex) {
      emits('onCancel');
   }
}

onMounted(initializeModal);
//#endregion

//#region permission
const disabled = computed(() => {
   if (!props.initialWorkCycle) return false;
   if (!checkAccess('update', props.initialWorkCycle?.scopes)) return true;
   return props.loading;
});

const modalTitle = computed(() =>
   props.initialWorkCycle
      ? t('actions.change-type', { name: t('work-cycles.work-cycle', 1)?.toLowerCase() })
      : t('actions.add-type', { name: t('work-cycles.work-cycle', 1)?.toLowerCase() }),
);

const primaryActionText = computed(() => (props.initialWorkCycle ? t('actions.change') : t('actions.add')));

function handleSave(workCycle: WorkCycle) {
   emits('onSave', mapWorkCyclePayloadForSave(workCycle, formatTimetable));
}

function mapErrors(errors: { [key: string]: string }) {
   return mapWorkCycleFormErrors(errors);
}
//#endregion
</script>

<template>
   <NxtGenericFormModal
      v-if="isModalReady"
      :key="`work-cycle-modal-${formMountKey}`"
      class="work-cycle-modal"
      :data="modalInitialData"
      :modal-type="'custom'"
      :custom-title="modalTitle"
      :primary-action-text="primaryActionText"
      :loading="loading || isInitializing"
      :schema="WORK_CYCLE_SCHEMA"
      @close="emits('onCancel')"
      @save="handleSave"
   >
      <template #default="{ modelValue, errors, loading: formLoading }">
         <div class="work-cycle-modal__content">
            <div class="row mb-3">
               <NxtInput
                  v-model="modelValue.name"
                  :class="'col-4'"
                  :name="'name'"
                  :label="$t('work-cycles.work-cycle', 1)"
                  :required="true"
                  :errors="mapErrors(errors)"
                  :disabled="disabled || formLoading"
               />
            </div>

            <div class="mb-3">
               <NxtWarningMessage
                  v-if="(modelValue.worksheets?.length || 0) < 2"
                  :model-value="(modelValue.worksheets?.length || 0) < 2 ? undefined : true"
                  :name="'work_cycle_length'"
                  :required="true"
                  :errors="mapErrors(errors)"
               >
                  {{ $t('work-cycles.two-work-schedules-required') }}
               </NxtWarningMessage>
            </div>

            <Draggable
               v-model="modelValue.worksheets"
               :class="'work-cycle-modal__worksheets'"
               :item-key="'id'"
               :component-data="{ tag: 'ul', type: 'transition-group', name: drag ? 'flip-list' : null }"
               :animation="200"
               :ghost-class="'-ghost'"
               :scroll-sensitivity="100"
               :handle="'.handle'"
               :force-fallback="true"
               @start="drag = true"
               @end="drag = false"
            >
               <template #item="{ index, element }: { index: number; element: Worksheet }">
                  <ContractExtensionWorksheet
                     :key="index"
                     :customer="customer"
                     :worksheet-index="index"
                     :worksheet="element"
                     :errors="mapErrors(errors)"
                     :loading="disabled || formLoading"
                     :is-initializing="isInitializing"
                     :shifts="shifts"
                     :cost-centers="costCenters"
                     :invoice-groups="invoiceGroups"
                     @on-delete-worksheet="deleteWorksheet(modelValue, $event)"
                     @on-duplicate-worksheet="duplicateWorksheet(modelValue, $event)"
                     @on-update-worksheet="updateWorkSheet(modelValue, $event)"
                  />
               </template>
            </Draggable>

            <NxtLink class="work-cycle-modal__add" @on-click="addWorksheet(modelValue)">
               + {{ t('actions.add-type', { name: t('work-cycles.work-schedule', 1) })?.toLowerCase() }}
            </NxtLink>
         </div>
      </template>
   </NxtGenericFormModal>
</template>

<style lang="scss">
.work-cycle-modal__add {
   margin-top: var(--nxt-gutter);
}

.work-cycle-modal__worksheets {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}
</style>
